import {
  type ReplayTextField as ReplayTextFieldSpec,
  type ReplayTextProblem,
  problemSentence,
} from '@/lib/replay-settings';
import { cn } from '@/lib/utils';
import { TextArea } from '@pms-digital/design-system';
import { useId } from 'react';

/**
 * One text of the instruction (docs/specs/rejeu.md §10.3): its label, its
 * « n / max caractères » counter, the line that says when it is served, and the
 * field.
 *
 * ## A bound that is SHOWN, not enforced by the field
 *
 * No `maxLength`: the other texts of Réglages cut a paste at the bound, but here
 * the refusal is part of the screen (« Refus de validation rendus en ligne »).
 * A field that silently dropped the tail of a pasted instruction would hide the
 * very thing the operator has to decide - which paragraph goes - so the text
 * stays whole, the counter goes red and the sentence under the field says why
 * the save is closed.
 *
 * ## What moved, and the package `TextArea`
 *
 * The planche paints a text that differs from the one in force yellow (`jaune-deep`
 * border on `jaune-tint`). The package `TextArea` has no tinted state and takes no
 * `className`: the box under the fields names the touched texts, and `data-touched` stays
 * on the control. The hint is the field's `help` (under it now, where the planche drew it
 * above), and a refusal sets `status="error"` - the red border and `aria-invalid` - while
 * its sentence stays this file's own paragraph, so the hint is still read beside it.
 */

export interface ReplayTextFieldProps {
  readonly field: ReplayTextFieldSpec;
  readonly value: string;
  /** The text differs from the one in force. */
  readonly touched: boolean;
  /** What the server would refuse in it, or null. */
  readonly problem: ReplayTextProblem | null;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}

export function ReplayTextField({
  field,
  value,
  touched,
  problem,
  disabled,
  onChange,
}: ReplayTextFieldProps) {
  const id = useId();
  const problemId = `${id}-refus`;
  const over = problem === 'too-long';

  return (
    <div className="relative flex min-w-0 flex-col gap-1.5">
      {/* The counter on the label's line, at its right end, outside the label. */}
      <span
        data-testid={`rejeu-compteur-${field.key}`}
        className={cn(
          'absolute top-0 right-0 text-caption tabular-nums',
          over ? 'font-semibold text-rouge-deep' : 'text-gray-600',
        )}
      >
        {value.length} / {field.max} caractères
      </span>
      <TextArea
        id={id}
        label={field.label}
        help={field.hint}
        status={problem === null ? 'default' : 'error'}
        data-testid={`rejeu-texte-${field.key}`}
        data-touched={touched ? 'true' : 'false'}
        value={value}
        rows={field.rows}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      {problem === null ? null : (
        <p
          id={problemId}
          role="alert"
          data-testid={`rejeu-refus-${field.key}`}
          className="text-rouge-deep text-small"
        >
          {problemSentence(field.key, problem)}
        </p>
      )}
    </div>
  );
}
